import React, { useEffect, useState } from 'react';
import { useAuth } from './contexts/AppAuthContext';
import axios from 'axios';
import PixPaymentModal from './components/PixPaymentModal';
import CouponsContent from './CouponsContent';
import AppearanceSettings from './AppearanceSettings';
import StockManagement from './StockManagement';
import TeamManagementContent from './TeamManagement';
import FinancialMetricsPanel from './FinancialMetricsPanel';
import { useWhatsAppIntegration } from './hooks/useWhatsAppIntegration';
import { MasterAIErrorLogsContent, MasterAIUsageContent, MasterAIKeyPoolContent, MasterActionLogsContent, MasterAnalyticsContent, MasterBackupContent, MasterGalleryContent, MasterNotificationSoundsContent, MasterProspectionContent, MasterReportsContent, MasterRestaurantsContent, MasterSystemSettingsContent, MasterTemplatesContent, MasterUazAPIContent } from './MasterPanel';
import { AdditionalGroupsContent, AdditionalsContent, CashbackContent, CategoriesContent, CustomersContent, DeliveryZonesContent, MyPlanContent, OrdersContent, WhatsAppContent } from './components/content';
import KDSScreen from './components/content/KDSScreen';
import DeliveryDriversContent from './components/content/DeliveryDriversContent';
import CashRegisterContent from './components/content/CashRegisterContent';
import ReportsContent from './components/content/ReportsContent';
import TableManagementContent from './components/content/TableManagementContent';
import SettingsContent from './components/content/SettingsContent';
import ProductsContent from './components/content/ProductsContent';
import { AlertTriangle, Bell, Bike, Bot, Boxes, Check, CheckSquare, ChefHat, ChevronLeft, ChevronRight, Clock, DollarSign, Download, FileText, Image as ImageIcon, KeyRound, LogOut, MessageSquare, Package, Palette, Plus, RefreshCw, Search, Settings, Shield, ShoppingCart, Sparkles, Star, Store, Tag, Target, Ticket, TrendingUp, Truck, User, UsersRound, Wallet, Zap } from 'lucide-react';
import { Switch } from './components/ui/switch';
import { toast } from 'sonner';
import { getBackendUrl } from './utils/api';

const API = `${getBackendUrl()}/api`;

const Dashboard = () => {
  // FORCE CACHE INVALIDATION 20260120
  // IMPORTANT: useAuth must be the first hook call to ensure user/restaurant are available
  const { user, logout, restaurant, setRestaurant, setUser } = useAuth();
  
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showPixPaymentModal, setShowPixPaymentModal] = useState(false); // Use existing PixPaymentModal
  const [subscriptionPrice, setSubscriptionPrice] = useState(99.00); // Preço da assinatura do sistema
  const [isCloseOrderModalOpen, setIsCloseOrderModalOpen] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false); // 🆕 Elevado para Dashboard
  const [isPDVOpen, setIsPDVOpen] = useState(false);
  
  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [masterData, setMasterData] = useState(null); // Store master admin data when impersonating

  // Check if user is master admin
  const isMasterAdmin = restaurant?.is_master === true;
  
  // ===== RBAC: role do usuário membro logado =====
  const userRole = restaurant?.user_role || 'gestor';
  const isGestor = userRole === 'gestor' || userRole === 'admin';
  const isCaixa = userRole === 'caixa';
  const isGarcom = userRole === 'garcom';

  // Mapa de seções permitidas por role (gestor vê tudo)
  const SECTIONS_BY_ROLE = {
    caixa: new Set(['orders', 'cash-register', 'tables', 'reports', 'stock']),
    garcom: new Set(['orders', 'tables', 'products']),
    cozinha: new Set(['kds']),
  };
  
  const canSeeSection = (id) => {
    if (isGestor) return true;
    const allowed = SECTIONS_BY_ROLE[userRole];
    return allowed ? allowed.has(id) : false;
  };
  
  // Check if restaurant is expired or suspended
  const isExpired = restaurant?.is_expired === true;
  const isSuspended = restaurant?.is_suspended === true;
  const isRestricted = isExpired || isSuspended;

  // Store status (open/closed manually)
  const [storeStatus, setStoreStatus] = useState({
    manually_closed: false,
    is_open: true,
    loading: false
  });

  // AI Quick Toggle status (atalho no header)
  const [aiQuickToggle, setAiQuickToggle] = useState({
    enabled: false,
    loading: false
  });

  // WhatsApp Integration Hook
  const whatsappIntegration = useWhatsAppIntegration();

  // KDS: mostra o menu ao gestor apenas quando existe pelo menos 1 chefe de cozinha ativo
  const [hasKitchenStaff, setHasKitchenStaff] = useState(false);

  // Set default active section based on user type and role
  const [activeSection, setActiveSection] = useState(
    isMasterAdmin ? 'restaurants' : (userRole === 'cozinha' ? 'kds' : 'orders')
  );

  const _allMenuItems = isMasterAdmin ? [
    { id: 'restaurants', label: 'Gestão de Lojas', icon: Store, gradient: 'from-blue-500 to-cyan-500' },
    { id: 'financial', label: 'Financeiro / MRR', icon: DollarSign, gradient: 'from-emerald-500 to-green-600' },
    { id: 'analytics', label: 'Análises & Métricas', icon: TrendingUp, gradient: 'from-emerald-500 to-teal-500' },
    { id: 'ai-usage', label: 'Consumo de IA', icon: Sparkles, gradient: 'from-fuchsia-500 to-purple-600' },
    { id: 'ai-key-pool', label: 'Chaves da IA', icon: KeyRound, gradient: 'from-yellow-400 to-amber-500' },
    { id: 'reports', label: 'Relatórios Financeiros', icon: FileText, gradient: 'from-orange-500 to-amber-500' },
    { id: 'ai-errors', label: 'Erros da IA', icon: AlertTriangle, gradient: 'from-red-500 to-orange-500' },
    { id: 'templates', label: 'Templates', icon: CheckSquare, gradient: 'from-cyan-500 to-blue-500' },
    { id: 'gallery', label: 'Galeria de Imagens', icon: ImageIcon, gradient: 'from-pink-500 to-rose-500' },
    { id: 'notification-sounds', label: 'Sons de Notificação', icon: Bell, gradient: 'from-amber-500 to-yellow-500' },
    { id: 'prospection', label: 'Prospecção Ativa', icon: Target, gradient: 'from-violet-500 to-purple-500' },
    { id: 'uazapi', label: 'UazAPI WhatsApp', icon: MessageSquare, gradient: 'from-green-500 to-teal-500' },
    { id: 'backup', label: 'Backup / Restore', icon: Download, gradient: 'from-cyan-500 to-blue-500' },
    { id: 'action-logs', label: 'Histórico', icon: Clock, gradient: 'from-indigo-500 to-blue-600' },
    { id: 'system-settings', label: 'Configurações do Sistema', icon: Settings, gradient: 'from-purple-500 to-pink-500' }
  ] : [
    { id: 'orders', label: 'Pedidos', icon: ShoppingCart, gradient: 'from-orange-500 to-amber-500' },
    { id: 'kds', label: 'KDS (Cozinha)', icon: ChefHat, gradient: 'from-red-500 to-rose-600' },
    { id: 'cash-register', label: 'Caixa', icon: Wallet, gradient: 'from-green-500 to-emerald-500' },
    { id: 'tables', label: 'Controle de Mesas', icon: Store, gradient: 'from-indigo-500 to-violet-500' },
    { id: 'products', label: 'Produtos', icon: Package, gradient: 'from-emerald-500 to-teal-500' },
    { id: 'categories', label: 'Categorias', icon: Tag, gradient: 'from-violet-500 to-purple-500' },
    { id: 'additional-groups', label: 'Grupos de Adicionais', icon: Package, gradient: 'from-cyan-500 to-blue-500' },
    { id: 'additionals', label: 'Adicionais', icon: Plus, gradient: 'from-amber-500 to-orange-500' },
    { id: 'stock', label: 'Estoque', icon: Boxes, gradient: 'from-orange-500 to-red-500' },
    { id: 'delivery-zones', label: 'Bairros e Taxas', icon: Truck, gradient: 'from-teal-500 to-cyan-500' },
    { id: 'delivery-drivers', label: 'Entregadores', icon: Bike, gradient: 'from-purple-500 to-violet-500' },
    { id: 'customers', label: 'Clientes', icon: User, gradient: 'from-indigo-500 to-blue-600' },
    { id: 'cashback', label: 'Cashback', icon: Star, gradient: 'from-yellow-500 to-amber-500' },
    { id: 'coupons', label: 'Cupons', icon: Ticket, gradient: 'from-purple-500 to-pink-500' },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, gradient: 'from-green-500 to-emerald-500' },
    { id: 'appearance', label: 'Aparência', icon: Palette, gradient: 'from-pink-500 to-rose-500' },
    { id: 'team', label: 'Equipe', icon: UsersRound, gradient: 'from-fuchsia-500 to-purple-600' },
    { id: 'my-plan', label: 'Meu Plano', icon: Star, gradient: 'from-blue-500 to-indigo-500' },
    { id: 'reports', label: 'Relatórios', icon: TrendingUp, gradient: 'from-green-500 to-emerald-600' },
    { id: 'settings', label: 'Configurações', icon: Settings, gradient: 'from-slate-500 to-gray-600' }
  ];

  // Filtrar menu por role (gestor vê tudo; caixa/garçom veem apenas seções permitidas)
  const menuItems = isMasterAdmin
    ? _allMenuItems
    : _allMenuItems.filter((it) => {
        if (!canSeeSection(it.id)) return false;
        if (it.id === 'kds' && isGestor && !hasKitchenStaff) return false;
        return true;
      });

  // Descobrir se há chefes de cozinha (para mostrar o menu KDS ao gestor)
  useEffect(() => {
    let active = true;
    if (!isMasterAdmin && isGestor) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/team/users`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          if (!active) return;
          const users = res.data?.users || [];
          setHasKitchenStaff(users.some((u) => u.role === 'cozinha' && u.active !== false));
        })
        .catch(() => {});
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMasterAdmin, isGestor, activeSection]);

  // Se a seção atual não é permitida para este role, redirecionar para a inicial da role
  useEffect(() => {
    if (!isMasterAdmin && !isGestor && activeSection && !canSeeSection(activeSection)) {
      setActiveSection(userRole === 'cozinha' ? 'kds' : 'orders');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, activeSection]);

  // Show expired modal when restaurant is expired/suspended
  useEffect(() => {
    if (!isMasterAdmin && isRestricted) {
      setShowExpiredModal(true);
    }
  }, [isRestricted, isMasterAdmin]);

  // Fetch subscription price when expired modal is shown
  useEffect(() => {
    const fetchSubscriptionPrice = async () => {
      if (showExpiredModal || showPixPaymentModal) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/restaurant/plan-info`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data?.subscription_price) {
            setSubscriptionPrice(response.data.subscription_price);
          }
        } catch (error) {
          console.error('Error fetching subscription price:', error);
          // Keep default 99.00 on error
        }
      }
    };
    fetchSubscriptionPrice();
  }, [showExpiredModal, showPixPaymentModal]);

  // Handle successful payment - refresh restaurant data
  const handlePaymentSuccess = async () => {
    try {
      const response = await axios.get(`${API}/me`);
      if (response.data) {
        setRestaurant(response.data.restaurant);
      }
      setShowExpiredModal(false);
      setShowPixPaymentModal(false);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };
  
  // Handle 403 - Expired/Suspended restaurant (não para master admin)
  // Usando useEffect para reagir a erros 403 de forma controlada
  useEffect(() => {
    const handle403Interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Apenas tratar 403 para mostrar modal de expiração
        if (error.response?.status === 403 && !isMasterAdmin) {
          const message = error.response?.data?.detail || 'Acesso negado';
          if (message.includes('expirado') || message.includes('suspensa')) {
            setShowExpiredModal(true);
          }
        }
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(handle403Interceptor);
    };
  }, [isMasterAdmin]);

  const handleMenuClick = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false); // Close mobile menu when item is selected
  };

  const createSampleData = async () => {
    try {
      const response = await axios.post(`${API}/sample-data`);
      const data = response.data;
      
      // Show detailed success message
      const summary = data.summary;
      const message = `✅ Dados demonstrativos criados com sucesso!\n\n` +
        `📋 Resumo:\n` +
        `• ${summary.categories} Categorias\n` +
        `• ${summary.products} Produtos\n` +
        `• ${summary.additional_groups} Grupos de Adicionais\n` +
        `• ${summary.additionals} Adicionais\n` +
        `• ${summary.orders} Pedidos\n\n` +
        `Você pode recriar os dados a qualquer momento clicando novamente no botão.`;
      
      alert(message);
    } catch (error) {
      console.error('Error creating sample data:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao criar dados de exemplo';
      alert(`❌ Erro: ${errorMsg}`);
    }
  };

  // Store status functions (open/close manually)
  const fetchStoreStatus = async () => {
    if (isMasterAdmin && !isImpersonating) return;
    try {
      const response = await axios.get(`${API}/restaurant/store-status`);
      setStoreStatus({
        manually_closed: response.data.manually_closed,
        is_open: response.data.is_open,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching store status:', error);
    }
  };

  const toggleStoreStatus = async () => {
    if (storeStatus.loading) return;
    setStoreStatus(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await axios.post(`${API}/restaurant/toggle-store-status`);
      setStoreStatus({
        manually_closed: response.data.manually_closed,
        is_open: response.data.is_open,
        loading: false
      });
      
      toast.success(response.data.message, {
        description: response.data.manually_closed 
          ? 'A loja foi fechada manualmente. Clientes não poderão fazer pedidos.'
          : 'A loja foi aberta. Clientes podem fazer pedidos normalmente.',
        duration: 4000
      });
    } catch (error) {
      console.error('Error toggling store status:', error);
      toast.error('Erro ao alterar status da loja', {
        description: error.response?.data?.detail || 'Tente novamente',
        duration: 4000
      });
      setStoreStatus(prev => ({ ...prev, loading: false }));
    }
  };

  // Fetch store status on load
  useEffect(() => {
    if (user && (!isMasterAdmin || isImpersonating)) {
      fetchStoreStatus();
      fetchAiQuickToggleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isMasterAdmin, isImpersonating]);

  // Fetch AI status for quick toggle
  const fetchAiQuickToggleStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/ai-config`);
      setAiQuickToggle(prev => ({
        ...prev,
        enabled: response.data?.enabled || false
      }));
    } catch (error) {
      console.error('Error fetching AI status:', error);
    }
  };

  // Toggle AI on/off (quick shortcut in header)
  const toggleAiQuick = async () => {
    if (aiQuickToggle.loading) return;
    setAiQuickToggle(prev => ({ ...prev, loading: true }));
    
    try {
      const newStatus = !aiQuickToggle.enabled;
      await axios.put(`${API}/whatsapp/ai-config`, { enabled: newStatus });
      
      setAiQuickToggle({
        enabled: newStatus,
        loading: false
      });
      
      toast.success(
        newStatus ? '🤖 IA Atendente ATIVADA!' : '🤖 IA Atendente desativada',
        {
          description: newStatus 
            ? 'A IA está respondendo clientes no WhatsApp' 
            : 'A IA não responderá mais automaticamente',
          duration: 3000
        }
      );
    } catch (error) {
      console.error('Error toggling AI:', error);
      toast.error('Erro ao alterar status da IA', {
        description: error.response?.data?.detail || 'Tente novamente'
      });
      setAiQuickToggle(prev => ({ ...prev, loading: false }));
    }
  };

  // Impersonation functions
  const handleImpersonate = (impersonationData) => {
    // Save current master admin data (persistido para sobreviver a reloads)
    const masterBackup = {
      token: localStorage.getItem('token'),
      refresh_token: localStorage.getItem('refresh_token'),
      restaurant: restaurant
    };
    setMasterData(masterBackup);
    try { localStorage.setItem('masterBackup', JSON.stringify(masterBackup)); } catch (e) { /* ignore */ }
    
    // Switch to restaurant view
    const { access_token, refresh_token, restaurant: restaurantData } = impersonationData;
    
    localStorage.setItem('token', access_token);
    // BUGFIX: usar o refresh_token ESCOPADO À LOJA (se o backend enviou). Assim, uma
    // renovação de token durante a impersonação gera um token da LOJA, não do master.
    if (refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
    } else {
      localStorage.removeItem('refresh_token');
    }
    localStorage.setItem('restaurant', JSON.stringify(restaurantData));
    localStorage.setItem('isImpersonating', 'true');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    setUser({ token: access_token });
    setRestaurant(restaurantData);
    setIsImpersonating(true);
    setActiveSection('orders');
  };

  const handleExitImpersonate = () => {
    // Recupera o backup do master do state ou do localStorage (sobrevive a reload)
    let backup = masterData;
    if (!backup || !backup.token) {
      try { backup = JSON.parse(localStorage.getItem('masterBackup')); } catch (e) { backup = null; }
    }
    if (!backup || !backup.token) {
      console.error('No master data found');
      return;
    }
    
    // Restore master admin data
    localStorage.setItem('token', backup.token);
    if (backup.refresh_token) {
      localStorage.setItem('refresh_token', backup.refresh_token);
    } else {
      localStorage.removeItem('refresh_token');
    }
    localStorage.setItem('restaurant', JSON.stringify(backup.restaurant));
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('masterBackup');
    axios.defaults.headers.common['Authorization'] = `Bearer ${backup.token}`;
    
    setUser({ token: backup.token });
    setRestaurant(backup.restaurant);
    setIsImpersonating(false);
    setMasterData(null);
    setActiveSection('restaurants');
  };

  // Check if we're returning from impersonation (page reload)
  useEffect(() => {
    const wasImpersonating = localStorage.getItem('isImpersonating');
    // Only set impersonating if there's a valid session and token
    if (wasImpersonating === 'true' && localStorage.getItem('token')) {
      setIsImpersonating(true);
      // Restaura o backup do master para que "Sair" funcione mesmo após reload
      if (!masterData) {
        try {
          const backup = JSON.parse(localStorage.getItem('masterBackup'));
          if (backup && backup.token) setMasterData(backup);
        } catch (e) { /* ignore */ }
      }
    } else {
      // Clean up stale impersonation flag if no valid session
      localStorage.removeItem('isImpersonating');
      setIsImpersonating(false);
    }
  }, []);

  return (
    <div className="h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 flex overflow-hidden">
      <div className="floating-elements">
        <div className="floating-circle floating-circle-1"></div>
        <div className="floating-circle floating-circle-2"></div>
        <div className="floating-circle floating-circle-3"></div>
      </div>

      {/* Mobile backdrop */}
      {mobileMenuOpen && !isCloseOrderModalOpen && !isOrderModalOpen && !isPDVOpen && (
        <div 
          className="mobile-backdrop visible md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Oculta quando modal de pedidos, finalizar pedido ou PDV estiver aberto */}
      {!isCloseOrderModalOpen && !isOrderModalOpen && !isPDVOpen && (
        <div className={`sidebar ${collapsed ? 'sidebar-collapsed' : 'sidebar-expanded'} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="flex items-center">
            <div className="relative">
              {isMasterAdmin ? (
                <Shield className="w-8 h-8 text-purple-500" />
              ) : (
                <>
                  <ChefHat className="w-8 h-8 text-orange-500" />
                  <Zap className="w-4 h-4 text-yellow-400 absolute -top-1 -right-1" />
                </>
              )}
            </div>
            {(!collapsed || mobileMenuOpen) && (
              <div className="ml-3">
                <h2 className="text-xl font-bold text-white">Pede é Fácil</h2>
                <p className="text-xs text-gray-400">
                  {isMasterAdmin ? 'Painel Master' : 'Painel Digital'}
                </p>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="collapse-button hidden md:flex"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>

          {/* Mobile close button */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="collapse-button md:hidden"
          >
            <ChevronLeft size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item.id)}
              className={`menu-item ${activeSection === item.id ? 'menu-item-active' : ''}`}
              title={collapsed && !mobileMenuOpen ? item.label : ''}
            >
              <div className={`menu-icon bg-gradient-to-r ${item.gradient}`}>
                <item.icon size={20} />
              </div>
              {(!collapsed || mobileMenuOpen) && <span className="whitespace-nowrap">{item.label}</span>}
              {activeSection === item.id && <div className="active-indicator"></div>}
            </button>
          ))}
        </nav>
        </div>
      )}

      {/* Main Content */}
      <div className="main-content">
        {/* Header - Oculto quando modal de pedidos, finalizar pedido ou PDV estiver aberto */}
        {!isCloseOrderModalOpen && !isOrderModalOpen && !isPDVOpen && (
          <header className="header">
            <div className="flex items-center space-x-4">
              {/* Mobile hamburger menu */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="header-button mobile-menu-pulse md:hidden"
                data-testid="mobile-menu-button"
              >
                <ChevronRight size={18} />
              </button>

              <div className="search-container hidden sm:block">
                <Search className="search-icon" size={18} />
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="search-input"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Store Status Toggle Button - Only for non-master or impersonating */}
              {(!isMasterAdmin || isImpersonating) && (
                <button
                  onClick={toggleStoreStatus}
                  disabled={storeStatus.loading}
                  className={`header-button relative transition-all duration-300 ${
                    storeStatus.loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title={storeStatus.manually_closed ? 'Loja fechada manualmente - Clique para abrir' : 'Loja aberta - Clique para fechar'}
                  data-testid="store-status-toggle"
                >
                  {storeStatus.loading ? (
                    <RefreshCw size={18} className="animate-spin text-gray-400" />
                  ) : (
                    <Store 
                      size={18} 
                      className={`transition-colors duration-300 ${
                        storeStatus.manually_closed 
                          ? 'text-red-400 hover:text-red-300' 
                          : 'text-green-400 hover:text-green-300'
                      }`}
                    />
                  )}
                  {!storeStatus.loading && (
                    <div 
                      className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${
                        storeStatus.manually_closed ? 'bg-red-500' : 'bg-green-500'
                      } animate-pulse`}
                    />
                  )}
                </button>
              )}
              
              {/* AI Quick Toggle Button - Only for non-master or impersonating */}
              {(!isMasterAdmin || isImpersonating) && (
                <button
                  onClick={toggleAiQuick}
                  disabled={aiQuickToggle.loading}
                  className={`header-button relative transition-all duration-300 ${
                    aiQuickToggle.loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title={aiQuickToggle.enabled ? 'IA Atendente ATIVA - Clique para desativar' : 'IA Atendente desativada - Clique para ativar'}
                  data-testid="ai-quick-toggle"
                >
                  {aiQuickToggle.loading ? (
                    <RefreshCw size={18} className="animate-spin text-gray-400" />
                  ) : (
                    <Bot 
                      size={18} 
                      className={`transition-colors duration-300 ${
                        aiQuickToggle.enabled 
                          ? 'text-violet-400 hover:text-violet-300' 
                          : 'text-gray-500 hover:text-gray-400'
                      }`}
                    />
                  )}
                  {!aiQuickToggle.loading && (
                    <div 
                      className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${
                        aiQuickToggle.enabled ? 'bg-violet-500 animate-pulse' : 'bg-gray-600'
                      }`}
                    />
                  )}
                </button>
              )}
              
              <div className="flex items-center space-x-2 text-white">
                {isMasterAdmin && !isImpersonating && <Shield size={18} className="text-purple-400" />}
                <span className="text-sm hidden sm:inline">{restaurant?.name}</span>
                <User size={18} />
              </div>
              
              <button onClick={logout} className="header-button text-red-400 hover:text-red-300">
                <LogOut size={18} />
              </button>
            </div>
          </header>
        )}

        {/* Impersonation Banner - Oculto quando modal de finalizar pedido ou PDV estiver aberto */}
        {isImpersonating && !isCloseOrderModalOpen && !isPDVOpen && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 flex items-center justify-between shadow-lg">
            <div className="flex items-center space-x-3">
              <Shield size={20} className="animate-pulse" />
              <div>
                <p className="font-semibold">Modo Master: Acessando painel da loja</p>
                <p className="text-sm text-purple-100">Você está visualizando como: {restaurant?.name}</p>
              </div>
            </div>
            <button
              onClick={handleExitImpersonate}
              className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors backdrop-blur-sm"
              data-testid="exit-impersonate-button"
            >
              <ChevronLeft size={18} />
              <span className="font-medium">Voltar ao Painel Master</span>
            </button>
          </div>
        )}

        {/* Content Area */}
        <main className="content-area">
          {isMasterAdmin && !isImpersonating ? (
            // Master Admin Content
            <>
              {activeSection === 'restaurants' && <MasterRestaurantsContent onImpersonate={handleImpersonate} />}
              {activeSection === 'analytics' && <MasterAnalyticsContent />}
              {activeSection === 'ai-usage' && <MasterAIUsageContent />}
              {activeSection === 'ai-key-pool' && <MasterAIKeyPoolContent />}
              {activeSection === 'financial' && <FinancialMetricsPanel />}
              {activeSection === 'reports' && <MasterReportsContent />}
              {activeSection === 'ai-errors' && <MasterAIErrorLogsContent />}
              {activeSection === 'templates' && <MasterTemplatesContent />}
              {activeSection === 'action-logs' && <MasterActionLogsContent />}
              {activeSection === 'gallery' && <MasterGalleryContent />}
              {activeSection === 'notification-sounds' && <MasterNotificationSoundsContent />}
              {activeSection === 'prospection' && <MasterProspectionContent token={localStorage.getItem('token')} />}
              {activeSection === 'uazapi' && <MasterUazAPIContent />}
              {activeSection === 'backup' && <MasterBackupContent />}
              {activeSection === 'system-settings' && <MasterSystemSettingsContent />}
            </>
          ) : (
            // Restaurant Admin Content
            <>
              {activeSection === 'my-plan' && <MyPlanContent restaurant={restaurant} />}
              {activeSection === 'orders' && <OrdersContent isPDVOpen={isPDVOpen} setIsPDVOpen={setIsPDVOpen} />}
              {activeSection === 'kds' && <KDSScreen isGestor={isGestor} onExit={() => { if (isGestor) { setActiveSection('orders'); } else { logout(); } }} />}
              {activeSection === 'cash-register' && <CashRegisterContent />}
              {activeSection === 'tables' && <TableManagementContent isCloseOrderModalOpen={isCloseOrderModalOpen} setIsCloseOrderModalOpen={setIsCloseOrderModalOpen} isOrderModalOpen={isOrderModalOpen} setIsOrderModalOpen={setIsOrderModalOpen} setActiveSection={setActiveSection} />}
              {activeSection === 'products' && <ProductsContent />}
              {activeSection === 'categories' && <CategoriesContent />}
              {activeSection === 'additional-groups' && <AdditionalGroupsContent />}
              {activeSection === 'additionals' && <AdditionalsContent />}
              {activeSection === 'stock' && <StockManagement token={localStorage.getItem('token')} />}
              {activeSection === 'delivery-zones' && <DeliveryZonesContent />}
              {activeSection === 'delivery-drivers' && <DeliveryDriversContent />}
              {activeSection === 'customers' && <CustomersContent />}
              {activeSection === 'cashback' && <CashbackContent />}
              {activeSection === 'coupons' && <CouponsContent />}
              {activeSection === 'whatsapp' && <WhatsAppContent />}
              {activeSection === 'appearance' && <AppearanceSettings />}
              {activeSection === 'team' && <TeamManagementContent />}
              {activeSection === 'reports' && <ReportsContent />}
              {activeSection === 'settings' && <SettingsContent />}
            </>
          )}
        </main>
      </div>
      
      {/* Modal de Expiração */}
      {showExpiredModal && !isMasterAdmin && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
            overflow: 'auto'
          }}
        >
          <div 
            style={{
              backgroundColor: '#1f2937',
              borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              width: '100%',
              maxWidth: '400px',
              margin: 'auto',
              overflow: 'hidden',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            {/* Header com ícone */}
            <div style={{
              background: 'linear-gradient(to right, #f97316, #ef4444)',
              padding: '24px 16px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px auto'
              }}>
                <svg style={{ width: '28px', height: '28px', color: 'white' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', marginBottom: '8px' }}>
                {isSuspended ? '🔒 Loja Suspensa' : '⏰ Período Expirado'}
              </h2>
              <p style={{ color: '#fed7aa', fontSize: '13px' }}>
                {isSuspended ? 'Sua loja foi temporariamente suspensa' : 'O período de uso da sua loja expirou'}
              </p>
            </div>

            {/* Conteúdo */}
            <div style={{ padding: '16px' }}>
              <div style={{
                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                border: '1px solid rgba(249, 115, 22, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px'
              }}>
                <p style={{ color: '#d1d5db', textAlign: 'center', fontSize: '13px', lineHeight: '1.5' }}>
                  Para continuar utilizando o sistema, 
                  <span style={{ fontWeight: 'bold', color: '#fb923c' }}> renove sua mensalidade</span>.
                </p>
              </div>

              <div style={{
                backgroundColor: 'rgba(55, 65, 81, 0.5)',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid #374151',
                marginBottom: '12px'
              }}>
                <h3 style={{ color: 'white', fontWeight: '600', marginBottom: '8px', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#60a5fa', marginRight: '8px' }}>ℹ️</span>
                  Você ainda pode:
                </h3>
                <ul style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '24px' }}>
                  <li>✓ Visualizar seu painel</li>
                  <li>✓ Ver pedidos e histórico</li>
                  <li>✓ Acessar relatórios</li>
                </ul>
              </div>

              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <h3 style={{ color: 'white', fontWeight: '600', marginBottom: '8px', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#f87171', marginRight: '8px' }}>⚠️</span>
                  Atenção:
                </h3>
                <p style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '24px' }}>
                  Sua loja está <span style={{ fontWeight: 'bold', color: '#f87171' }}>fechada</span> e clientes não podem fazer pedidos.
                </p>
              </div>
            </div>

            {/* Botões */}
            <div style={{ padding: '0 16px 16px 16px' }}>
              {/* Botão Pagar com PIX */}
              <button
                onClick={() => {
                  setShowExpiredModal(false);
                  setShowPixPaymentModal(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  background: 'linear-gradient(to right, #22c55e, #16a34a)',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '14px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}
              >
                <svg style={{ width: '20px', height: '20px', marginRight: '8px' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93s3.06-7.44 7-7.93v15.86zm2 0V4.07c3.94.49 7 3.85 7 7.93s-3.06 7.44-7 7.93z"/>
                </svg>
                💳 Pagar com PIX
              </button>

              {/* Botão WhatsApp */}
              <a
                href={`https://wa.me/5538991504854?text=${encodeURIComponent('Olá! Preciso renovar a mensalidade da minha loja: ' + restaurant?.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: '2px solid #22c55e',
                  color: '#22c55e',
                  fontWeight: '500',
                  padding: '12px',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontSize: '13px',
                  marginBottom: '8px'
                }}
              >
                <svg style={{ width: '18px', height: '18px', marginRight: '8px' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Falar no WhatsApp
              </a>
              
              <button
                onClick={() => setShowExpiredModal(false)}
                style={{
                  width: '100%',
                  backgroundColor: '#374151',
                  color: 'white',
                  fontWeight: '500',
                  padding: '12px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Continuar Visualizando
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pagamento PIX - Reutiliza o componente existente */}
      <PixPaymentModal
        isOpen={showPixPaymentModal}
        onClose={() => {
          setShowPixPaymentModal(false);
          if (isRestricted) {
            setShowExpiredModal(true);
          }
        }}
        paymentData={{
          restaurant_id: restaurant?.id,
          months: 1,
          amount: subscriptionPrice
        }}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default Dashboard;
